import 'videojs-markers';
import 'videojs-markers/dist/videojs.markers.css';

import { cloneDeep } from 'lodash';
import * as _ from 'lodash';
import { useEffect, useRef } from 'react';
import videojs from 'video.js';

const DEFAULT_RETRY_INTERVAL_MS = 3000;

interface IProps {
  srcUrl: string;
  autoPlay?: boolean;
  /** Start muted once (e.g. for autoplay); user can unmute via player controls afterward. */
  muted?: boolean;
  disablePausing?: boolean;
  onTimeUpdate?: (currentTime: number) => void;
  markers?: any[];
  videoId?: string;
  /** Called when playback starts (`true`), or when maxWaitMs elapses without a successful load (`false`). */
  onPlaybackReady?: (success: boolean) => void;
  /** Max time to wait for first successful playback; -1 disables wait/retry logic. */
  maxWaitMs?: number;
  retryIntervalMs?: number;
  /** Keeps the player in the DOM but not visible until set false (e.g. while preview is loading). */
  hidden?: boolean;
}

function getVideoSourceType(url: string): string {
  if (/\.m3u8($|\?)/i.test(url)) {
    return 'application/x-mpegURL';
  }
  return 'video/mp4';
}

const defaultVideoJsOptions = {
  autoplay: false,
  controls: true,
  responsive: true,
  fluid: true,
  sources: [
    {
      src: null,
      type: 'video/mp4',
    },
  ],
};

const SimplePlayer = ({
  srcUrl,
  autoPlay = false,
  muted = false,
  disablePausing = false,
  onTimeUpdate = () => {},
  markers = [],
  videoId = 'player1',
  onPlaybackReady,
  maxWaitMs = -1,
  retryIntervalMs = DEFAULT_RETRY_INTERVAL_MS,
  hidden = false,
}: IProps) => {
  const videoRef = useRef(null);
  const playerRef = useRef<ReturnType<typeof videojs> | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxWaitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasSucceededRef = useRef(false);
  const hasReportedFailureRef = useRef(false);
  const hasAppliedInitialMuteRef = useRef(false);
  const srcUrlRef = useRef(srcUrl);
  const autoPlayRef = useRef(autoPlay);
  const mutedRef = useRef(muted);
  const hiddenRef = useRef(hidden);
  const maxWaitMsRef = useRef(maxWaitMs);
  const onPlaybackReadyRef = useRef(onPlaybackReady);

  srcUrlRef.current = srcUrl;
  autoPlayRef.current = autoPlay;
  mutedRef.current = muted;
  hiddenRef.current = hidden;
  maxWaitMsRef.current = maxWaitMs;
  onPlaybackReadyRef.current = onPlaybackReady;

  const waitEnabled = maxWaitMs > 0;

  const applyControlsVisibility = (player: ReturnType<typeof videojs> | null, isHidden: boolean) => {
    if (!player || typeof player.controls !== 'function') return;
    player.controls(!isHidden);
  };

  /** Mute once for autoplay policy; after that the user controls volume via the player UI. */
  const applyInitialMute = (player: ReturnType<typeof videojs>) => {
    if (!mutedRef.current || hasAppliedInitialMuteRef.current) return;
    player.muted(true);
    hasAppliedInitialMuteRef.current = true;
  };

  const clearSourceRetry = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  };

  const clearMaxWaitTimer = () => {
    if (maxWaitTimeoutRef.current) {
      clearTimeout(maxWaitTimeoutRef.current);
      maxWaitTimeoutRef.current = null;
    }
  };

  const buildSources = (url: string) => [
    {
      src: url,
      type: getVideoSourceType(url),
    },
  ];

  const reportSuccess = () => {
    if (hasSucceededRef.current) return;
    hasSucceededRef.current = true;
    clearSourceRetry();
    clearMaxWaitTimer();
    onPlaybackReadyRef.current?.(true);
  };

  const disposePlayer = () => {
    clearSourceRetry();
    clearMaxWaitTimer();
    if (playerRef.current?.dispose && _.isFunction(playerRef.current.dispose)) {
      playerRef.current.dispose();
      playerRef.current = null;
    }
  };

  const reportFailure = () => {
    if (hasSucceededRef.current || hasReportedFailureRef.current) return;
    hasReportedFailureRef.current = true;
    disposePlayer();
    onPlaybackReadyRef.current?.(false);
  };

  const scheduleSourceRetry = (player: ReturnType<typeof videojs>) => {
    if (!waitEnabled || !srcUrlRef.current || hasSucceededRef.current || hasReportedFailureRef.current) return;
    clearSourceRetry();
    retryTimeoutRef.current = setTimeout(() => {
      player.src(buildSources(srcUrlRef.current));
      player.load();
    }, retryIntervalMs);
  };

  const loadSource = (player: ReturnType<typeof videojs>, url: string) => {
    clearSourceRetry();
    player.src(buildSources(url));
    player.load();
  };

  const startMaxWaitTimer = () => {
    if (!waitEnabled || maxWaitMsRef.current <= 0) return;
    clearMaxWaitTimer();
    maxWaitTimeoutRef.current = setTimeout(() => {
      reportFailure();
    }, maxWaitMsRef.current);
  };

  const resetWaitState = () => {
    hasSucceededRef.current = false;
    hasReportedFailureRef.current = false;
    hasAppliedInitialMuteRef.current = false;
    clearSourceRetry();
    clearMaxWaitTimer();
  };

  const addListeners = (player: ReturnType<typeof videojs>) => {
    player.on('timeupdate', () => {
      onTimeUpdate(player.currentTime());
    });
    player.on('playing', () => {
      reportSuccess();
    });
    player.on('canplay', () => {
      applyInitialMute(player);
      if (autoPlayRef.current && player.paused() && !hasSucceededRef.current) {
        player.play().catch((error) => {
          console.warn('Autoplay was prevented by the browser. User interaction required.', error);
        });
      }
    });
    player.on('error', () => {
      if (waitEnabled) {
        console.warn('Player source not ready, retrying…', player.error());
        scheduleSourceRetry(player);
      } else {
        console.error('Player Error', player.error());
      }
    });
    player.on('waiting', () => {
      videojs.log('player is waiting');
    });
    player.on('dispose', () => {
      videojs.log('player will dispose');
      clearSourceRetry();
      clearMaxWaitTimer();
    });
  };

  const initialize = () => {
    setTimeout(() => {
      const myVideoJSOptions = {
        ...cloneDeep(defaultVideoJsOptions),
        sources: buildSources(srcUrl),
        autoplay: autoPlay,
        muted,
      };

      if (!playerRef.current) {
        const videoElement = videoRef.current;

        if (!videoElement) return;
        const player = (playerRef.current = videojs(videoElement, myVideoJSOptions, () => {
          videojs.log('player is ready');
          applyInitialMute(player);
          applyControlsVisibility(player, hiddenRef.current);
          addListeners(player);
        }));
        player.markers({
          markerStyle: {
            width: '8px',
            fontSize: '12px',
            'background-color': '#0db080',
          },
          markerTip: {
            display: false,
            style: {
              'font-size': '6px',
            },
            text(marker) {
              return marker.text || 'Marker';
            },
          },
          markers,
        });
      } else {
        loadSource(playerRef.current, srcUrl);
      }
    }, 0);
  };

  useEffect(() => {
    if (!srcUrl) return undefined;

    resetWaitState();
    if (waitEnabled) {
      startMaxWaitTimer();
    }
    initialize();

    return function cleanup() {
      disposePlayer();
    };
  }, [srcUrl, maxWaitMs, retryIntervalMs]);

  useEffect(() => {
    applyControlsVisibility(playerRef.current, hidden);
  }, [hidden]);

  const rootClassName = [
    'simple-player',
    hidden && 'simple-player--hidden simple-player--fill',
    !hidden && 'simple-player--fluid',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClassName}>
      <div className="simple-player__inner" data-vjs-player>
        <video id={videoId} ref={videoRef} className="video-js vjs-big-play-centered" playsInline />
      </div>
    </div>
  );
};

export default SimplePlayer;
